import React, { useEffect, useState } from 'react';
import ReportNote from '../components/ReportNote';

const API = process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000';

// YYYY-MM for the current month shifted by `offset` months
function monthStr(offset = 0) {
  const d = new Date();
  d.setMonth(d.getMonth() + offset, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function Cell({ cell }) {
  const color = cell.ok ? 'var(--text-1)' : 'var(--critical)';
  return (
    <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', whiteSpace: 'nowrap' }}>
      <span style={{ color, fontWeight: cell.ok ? 500 : 700 }}>{cell.logged_display}</span>
      <span style={{ color: 'var(--text-3)' }}> / {cell.required_display}</span>
    </td>
  );
}

export default function MonthlyTimeReport() {
  const [month, setMonth] = useState(monthStr(0));
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = (m) => {
    setLoading(true);
    fetch(`${API}/api/reports/monthly-time?month=${m}`)
      .then(r => r.json()).then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(month); }, [month]);

  const changeMonth = (delta) => {
    const [y, m] = month.split('-').map(Number);
    const d = new Date(y, m - 1 + delta, 1);
    setMonth(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`);
  };

  const printReport = () => window.print();

  return (
    <div>
      <ReportNote what="Time logged per person against the 7.8h per working day target." month={month} note="Target is month-to-date; weeks show in-month working days only." />
      {/* Controls */}
      <div className="no-print" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button onClick={() => changeMonth(-1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>‹</button>
          <span style={{ fontWeight: 700, fontSize: 15, minWidth: 160, textAlign: 'center' }}>
            {data ? data.label : month}
          </span>
          <button onClick={() => changeMonth(1)} className="btn btn-secondary" style={{ padding: '6px 12px' }}>›</button>
        </div>
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <button onClick={printReport} className="btn btn-secondary" style={{ gap: 6 }}>🖨 Print / PDF</button>
        </div>
      </div>

      {loading && <div className="loading">Loading time data…</div>}

      {!loading && data && (
        <>
          <div className="grid-4" style={{ marginBottom: 24 }}>
            <div className="card">
              <div className="card-title">Required / Person</div>
              <div className="card-value">{data.month_required_display}</div>
              <div className="card-sub">
                {data.hours_per_workday}h × working days
                {data.is_current_month ? ` · to ${data.as_of.slice(8, 10)}/${data.as_of.slice(5, 7)}` : ''}
              </div>
            </div>
            <div className="card">
              <div className="card-title">Working Days</div>
              <div className="card-value">{data.weeks.reduce((a, w) => a + w.working_days, 0)}</div>
              <div className="card-sub">Mon–Fri, excl. Belgian holidays</div>
            </div>
            <div className="card">
              <div className="card-title">Team Logged</div>
              <div className="card-value" style={{ color: 'var(--accent)' }}>
                {(() => {
                  const s = data.members.reduce((a, m) => a + m.total.logged_seconds, 0);
                  const h = Math.floor(s / 3600), mn = Math.round((s % 3600) / 60);
                  return mn ? `${h}h ${mn}m` : `${h}h`;
                })()}
              </div>
              <div className="card-sub">all worklogs, no exclusions</div>
            </div>
            <div className="card">
              <div className="card-title">On Target</div>
              <div className="card-value">
                {data.members.filter(m => m.total.ok).length}/{data.members.length}
              </div>
              <div className="card-sub">people meeting the month target</div>
            </div>
          </div>

          <div className="card">
            <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 18, fontFamily: 'Syne, sans-serif' }}>
              Logged Time vs Target — {data.label}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Name</th>
                    {data.weeks.map(w => (
                      <th key={w.start} style={{ textAlign: 'right' }}>{w.label}</th>
                    ))}
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                  <tr style={{ fontSize: 11, color: 'var(--text-3)' }}>
                    <th style={{ fontWeight: 400 }}>target →</th>
                    {data.weeks.map(w => (
                      <th key={w.start} style={{ textAlign: 'right', fontWeight: 400, fontFamily: 'DM Mono, monospace' }}>
                        {w.required_display}
                      </th>
                    ))}
                    <th style={{ textAlign: 'right', fontWeight: 400, fontFamily: 'DM Mono, monospace' }}>
                      {data.month_required_display}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {data.members.map(m => (
                    <tr key={m.name}>
                      <td style={{ fontWeight: 600 }}>{m.name}</td>
                      {m.weeks.map((c, i) => <Cell key={i} cell={c} />)}
                      <td style={{ textAlign: 'right', fontFamily: 'DM Mono, monospace', whiteSpace: 'nowrap', borderLeft: '1px solid var(--border)' }}>
                        <span style={{ color: m.total.ok ? 'var(--text-1)' : 'var(--critical)', fontWeight: 700 }}>
                          {m.total.logged_display}
                        </span>
                        <span style={{ color: 'var(--text-3)' }}> / {m.total.required_display}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div style={{ marginTop: 14, fontSize: 11, color: 'var(--text-3)', borderTop: '1px solid var(--border)', paddingTop: 10, display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <span>Each cell: logged / required ({data.hours_per_workday}h per working day)</span>
              <span><span style={{ color: 'var(--critical)', fontWeight: 700 }}>Red</span> = below target</span>
              <span>Weeks show in-month days only</span>
              {data.is_current_month && <span style={{ marginLeft: 'auto' }}>Target is month-to-date ({data.as_of})</span>}
            </div>
          </div>
        </>
      )}

      <style>{`
        @media print {
          .no-print { display: none !important; }
          .sidebar, .topbar { display: none !important; }
          .main { margin: 0 !important; padding: 20px !important; }
        }
      `}</style>
    </div>
  );
}
